// src/services/auth/AuthServiceProvider.tsx

import React, { useEffect, useState, type ReactNode } from "react";
import { Auth0AuthService } from "./Auth0AuthService";
import { auth0Config } from "@/config/auth0Config";
import { AuthServiceContext } from "./context";
import type { AuthService } from "./AuthService";

interface AuthServiceProviderProps {
  children: ReactNode;
  /**
   * Optional: Provide a custom AuthService implementation
   * If not provided, Auth0AuthService will be used with config from auth0Config
   */
  authService?: AuthService;
}

/**
 * Provider component that initializes and provides AuthService to the app.
 *
 * This is the top-level wrapper for authentication.
 * It creates the AuthService instance, initializes it, and provides it via Context.
 *
 * USAGE:
 * ```tsx
 * <AuthServiceProvider>
 *   <App />
 * </AuthServiceProvider>
 * ```
 *
 * Or with custom service:
 * ```tsx
 * const customService = new SupabaseAuthService(config);
 * <AuthServiceProvider authService={customService}>
 *   <App />
 * </AuthServiceProvider>
 * ```
 */
export const AuthServiceProvider: React.FC<AuthServiceProviderProps> = ({
  children,
  authService,
}) => {
  const [service, setService] = useState<AuthService | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Use provided service or create Auth0 service
    const serviceInstance =
      authService ||
      new Auth0AuthService({
        domain: auth0Config.domain,
        clientId: auth0Config.clientId,
        audience: auth0Config.audience,
        redirectUri: auth0Config.redirectUri,
        scope: auth0Config.scope,
        cacheLocation: auth0Config.cacheLocation,
        useRefreshTokens: auth0Config.useRefreshTokens,
      });

    // Initialize the service
    serviceInstance
      .initialize()
      .then(() => {
        console.log("[AuthServiceProvider] Auth service initialized successfully");
        setService(serviceInstance);
        setIsInitialized(true);
      })
      .catch((error) => {
        console.error("[AuthServiceProvider] Failed to initialize auth service:", error);
        // Still set service so app can render (error state will be available via service.getError())
        setService(serviceInstance);
        setIsInitialized(true);
      });

    // Cleanup
    return () => {
      if ("destroy" in serviceInstance && typeof serviceInstance.destroy === "function") {
        (serviceInstance as Auth0AuthService).destroy();
      }
    };
  }, [authService]);

  // Don't render children until service is initialized
  // This prevents components from trying to use auth before it's ready
  if (!isInitialized || !service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-24 h-24 mx-auto">
              <div className="absolute inset-0 bg-purple-500/20 rounded-full animate-ping" />
              <div className="absolute inset-0 bg-purple-600/30 rounded-full animate-ping animation-delay-200" />
              <div className="relative flex items-center justify-center w-full h-full bg-gray-800/80 backdrop-blur-xl rounded-full border border-purple-500/20">
                <div className="text-4xl font-bold text-purple-400">Λ</div>
              </div>
            </div>
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">
            Initializing Authentication...
          </h2>
          <p className="text-gray-400 text-sm sm:text-base">
            Setting up secure connection
          </p>
        </div>
      </div>
    );
  }

  return (
    <AuthServiceContext.Provider value={service}>
      {children}
    </AuthServiceContext.Provider>
  );
};
